import { DragEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import clsx from 'clsx';

import { UploadButton } from '@/components/common';
import { Search } from '@/components/common/Search';
import { Label, LABEL_TYPE, Spinner } from '@/components/generic';
import TimeInput from '@/components/generic/TimeInput/TimeInput';
import { PAGE_SIZE, UPLOAD_PICKER_TYPES } from '@/constants';
import { AssetScope, AssetType } from '@/enums';
import { formatDuration } from '@/helpers';
import { useFileUpload } from '@/hooks';
import { AddSquareIcon, ClockIcon } from '@/resources';
import { SidebarAssets } from '@/resources/icons/sidebar';
import { useGetOrganizationAssets } from '@/services';
import {
  Asset,
  CreateEditPlaylistPayload,
  Playlist,
  PlaylistAsset,
  SelectOptionProps
} from '@/types';

import { AssetItem } from '../Event/Asset/AssetItem';
import { AssetTypeFilter } from '../Event/CeremonyTemplate/AssetTypeFilter';
import { AssetPickerModal } from './AssetPickerModal';
import { PlaylistAssetItem } from './PlaylistAssetItem';

type PlaylistMixDetailsProps = {
  currentPlaylist: Playlist;
  updatePlaylist: (payload: CreateEditPlaylistPayload) => void;
};

export const PlaylistMixDetails = ({
  currentPlaylist,
  updatePlaylist
}: PlaylistMixDetailsProps) => {
  const [t] = useTranslation('common');
  const { ref: inViewRef, inView } = useInView();
  const [selectedFilter, setSelectedFilter] = useState<SelectOptionProps | null>(null);
  const [searchText, setSearchText] = useState<string>();
  const defaultValue = 15;
  const [defaultDuration, setDefaultDuration] = useState<number>(defaultValue);
  const [isDropActive, setIsDropActive] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [showDragIconIndex, setShowDragIconIndex] = useState<number | null>(null);

  const {
    data: organizationAssetsData,
    isLoading: isOrganizationAssetsLoading,
    fetchNextPage: organizationAssetsFetchNextPage,
    hasNextPage: organizationAssetsHasNextPage,
    refetch: organizationAssetsRefetch
  } = useGetOrganizationAssets(
    PAGE_SIZE,
    true,
    currentPlaylist?.organizationId,
    searchText,
    [selectedFilter?.value] as string[],
    'CreatedAt',
    false
  );

  const { uploadFiles, isUploading } = useFileUpload({
    organizationId: String(currentPlaylist?.organizationId),
    pickerTypes: UPLOAD_PICKER_TYPES.MEDIA,
    isPublic: false,
    assetScope: AssetScope.ORGANIZATION,
    onCreatedAsset: () => {
      organizationAssetsRefetch();
    }
  });

  const searchCallback = (query: string) => {
    setSearchText(query);
  };
  const simpleAssetFilterOptions: { value: string; label: string }[] = useMemo(
    () => [
      { value: String(AssetType.IMAGE), label: t('image', { ns: 'common' }) },
      { value: String(AssetType.VIDEO), label: t('video', { ns: 'common' }) },
      { value: String(AssetType.AUDIO), label: t('audio', { ns: 'common' }) },
      { value: String(AssetType.TEXT_OVERLAY), label: t('textOverlay', { ns: 'common' }) }
    ],
    [t]
  );

  useEffect(() => {
    if (inView && organizationAssetsHasNextPage) {
      organizationAssetsFetchNextPage();
    }
  }, [inView, organizationAssetsHasNextPage]);

  const handleRemoveAsset = (playlistAssetId: number) => {
    const filteredAssets = currentPlaylist.playlistAssets.filter((pa) => pa.id !== playlistAssetId);

    const newPlaylistAssets = filteredAssets.map((pa, idx) => ({
      ...pa,
      order: idx + 1
    }));
    updatePlaylist({
      ...currentPlaylist,
      playlistAssets: newPlaylistAssets,
      totalDuration: newPlaylistAssets.reduce(
        (sum, pa) => sum + (pa.asset.duration ?? pa.duration ?? 0),
        0
      )
    });
  };

  const handleDurationUpdate = (playlistAssetId: number, newDuration: number) => {
    const currentDuration = currentPlaylist.playlistAssets.find(
      (pa) => pa.id === playlistAssetId
    )?.duration;
    if (newDuration === currentDuration) {
      return;
    }
    const updatedAssets = currentPlaylist.playlistAssets.map((pa) =>
      pa.id === playlistAssetId ? { ...pa, duration: newDuration } : pa
    );

    updatePlaylist({
      ...currentPlaylist,
      playlistAssets: updatedAssets,
      totalDuration: updatedAssets.reduce(
        (sum, pa) => sum + (pa.asset.duration ?? pa.duration ?? 0),
        0
      )
    });
  };

  const handleDropAsset = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDropActive(false);
    const assetJson = e.dataTransfer.getData('application/assetJson');
    if (!assetJson) {
      return;
    }
    const asset: Asset = JSON.parse(assetJson);

    const newPlaylistAssets = [
      ...currentPlaylist.playlistAssets,
      {
        id: null,
        playlistId: currentPlaylist.id,
        assetId: asset.id,
        asset,
        order: currentPlaylist.playlistAssets.length + 1,
        duration: asset.duration ?? defaultDuration
      }
    ];
    updatePlaylist({
      ...currentPlaylist,
      playlistAssets: newPlaylistAssets,
      totalDuration: newPlaylistAssets.reduce(
        (sum, pa) => sum + (pa.asset.duration ?? pa.duration ?? 0),
        0
      )
    });
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDropActive(true);
  };

  const handleDragLeave = () => {
    setIsDropActive(false);
  };

  const handleClickAsset = (asset: Asset, event: React.MouseEvent) => {
    event.preventDefault();
    setSelectedAssets((prev) =>
      prev.some((a) => a.id === asset.id) ? prev.filter((a) => a.id !== asset.id) : [...prev, asset]
    );
  };

  const handleSubmitAsset = (): void => {
    if (!selectedAssets.length) {
      return;
    }
    const newPlaylistAssets = [
      ...currentPlaylist.playlistAssets,
      ...selectedAssets.map((asset, idx) => ({
        id: null,
        playlistId: currentPlaylist.id,
        assetId: asset.id,
        asset,
        order: currentPlaylist.playlistAssets.length + idx + 1,
        duration: asset.duration ?? defaultDuration
      }))
    ];
    updatePlaylist({
      ...currentPlaylist,
      playlistAssets: newPlaylistAssets,
      totalDuration: newPlaylistAssets.reduce(
        (sum, pa) => sum + (pa.asset.duration ?? pa.duration ?? 0),
        0
      )
    });
    setShowAssetPicker(false);
    setSelectedAssets([]);
  };

  const handleFilterClick = (value: SelectOptionProps) => {
    if (selectedFilter?.value === value?.value) {
      setSelectedFilter(null);
    } else {
      setSelectedFilter(value);
    }
  };

  return (
    <div className='grid size-full grid-cols-1 gap-x-10 md:grid-cols-2'>
      {isOrganizationAssetsLoading ? (
        <Spinner />
      ) : (
        <div className='mb-1 hidden size-full max-h-[calc(100vh-150px)] flex-col items-center rounded-lg bg-color-light-blue md:flex md:max-h-[calc(100vh-220px)]'>
          <div className='flex w-full justify-between px-6 py-3'>
            <Label text={t('uploadedContent')} type={LABEL_TYPE.H2} />
            <UploadButton
              multiple
              text='uploadNew'
              pickerTypes={UPLOAD_PICKER_TYPES.MEDIA}
              showMaxFileSize={false}
              isUploading={isUploading}
              disabled={isUploading}
              icon={<SidebarAssets className='mr-1 size-5 text-primary-blue' />}
              onFilesSelected={uploadFiles}
            />
          </div>
          <div className='flex w-full px-6 pt-2'>
            <Search callback={searchCallback} />
          </div>
          <div className='grid w-full grid-cols-2 gap-4 px-6 py-5'>
            {simpleAssetFilterOptions.map((item) => (
              <AssetTypeFilter
                title={item.label}
                key={item.value}
                onClick={() => handleFilterClick(item)}
                isClicked={item.value === selectedFilter?.value}
              />
            ))}
          </div>
          <div className='no-scrollbar mb-5 grid grid-cols-3 gap-6 overflow-y-auto px-6'>
            {organizationAssetsData?.pages
              .flatMap((page) => page.data?.items)
              .map((asset: Asset) => (
                <div
                  key={asset.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/assetJson', JSON.stringify(asset));
                  }}>
                  <AssetItem
                    asset={asset}
                    assetType={asset.assetType}
                    canBeDeleted={false}
                    canBeDragged
                    selectedVideoUrl={''}
                  />
                </div>
              ))}
            <div ref={inViewRef}></div>
          </div>
        </div>
      )}
      <div className='mb-1 flex size-full max-h-[calc(100vh-150px)] flex-col items-center md:max-h-[calc(100vh-220px)]'>
        <div className='flex w-full items-center justify-between px-6 py-3'>
          <Label
            text={t(`Timeline (${formatDuration(currentPlaylist?.totalDuration)})`)}
            type={LABEL_TYPE.H2}
          />
          <div className='flex flex-row items-center justify-center gap-x-4'>
            <Label text={t('defaultTime') + ' :'} type={LABEL_TYPE.PARAGRAPH1} />
            <TimeInput
              label={''}
              setFieldValue={(_, value) => {
                if (typeof value !== 'number' || isNaN(value)) {
                  setDefaultDuration(defaultValue);
                } else {
                  setDefaultDuration(value);
                }
              }}
              fieldName={''}
              defaultValue={defaultValue}
              isRequired={false}
              icon={<ClockIcon className='size-4 font-bold text-primary-black md:size-5' />}
              hasSpinners={true}
            />
          </div>
        </div>
        <div className='w-full border-b border-secondary-blue-gray'></div>
        <div className='no-scrollbar my-2 flex w-full flex-col gap-4 overflow-y-auto py-2'>
          {currentPlaylist?.playlistAssets.map((pa: PlaylistAsset, index) => (
            <PlaylistAssetItem
              key={pa.id}
              playlistAsset={pa}
              index={index}
              onUpdate={(from, to) => {
                if (from === to) {
                  return;
                }
                const updatedAssets = [...currentPlaylist.playlistAssets];
                const [draggedItem] = updatedAssets.splice(from, 1);
                updatedAssets.splice(to, 0, draggedItem);
                const newPlaylistAssets = updatedAssets.map((pa, idx) => ({
                  ...pa,
                  order: idx + 1
                }));
                updatePlaylist({
                  ...currentPlaylist,
                  playlistAssets: newPlaylistAssets,
                  totalDuration: newPlaylistAssets.reduce(
                    (sum, pa) => sum + (pa.asset.duration ?? pa.duration ?? 0),
                    0
                  )
                });
              }}
              onRemove={handleRemoveAsset}
              onEditDuration={handleDurationUpdate}
              showDragIconIndex={index === showDragIconIndex}
              setShowDragIconIndex={setShowDragIconIndex}
            />
          ))}
        </div>
        <div
          className={clsx(
            'drop-zone hidden h-32 w-full flex-col justify-center gap-y-2 rounded-2xl border border-secondary-blue-gray py-8 transition-colors duration-200 md:flex',
            isDropActive ? 'bg-color-light-blue ring-4 ring-accent-light-blue' : 'bg-[#F3F6FD52]'
          )}
          data-drop-zone='true'
          onDrop={handleDropAsset}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}>
          <AddSquareIcon className='m-auto size-6 text-secondary-gray' />
          <Label
            text={t('dragAndDropAssetsHere')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='m-auto !text-center !text-secondary-gray'
          />
        </div>
        <div
          className={
            'flex h-32 w-full cursor-pointer flex-col justify-center gap-y-2 rounded-2xl border border-secondary-blue-gray bg-[#F3F6FD52] py-8 transition-colors duration-200 hover:bg-color-light-blue md:hidden'
          }
          onClick={() => setShowAssetPicker(true)}>
          <AddSquareIcon className='m-auto size-6 text-secondary-gray' />
          <Label
            text={t('dragAndDropAssetsHere')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='m-auto !text-center !text-secondary-gray'
          />
        </div>
        {showAssetPicker && (
          <AssetPickerModal
            isOpen={showAssetPicker}
            onClose={() => {
              setShowAssetPicker(false);
              setSelectedAssets([]);
            }}
            handleSubmit={handleSubmitAsset}
            searchCallback={searchCallback}
            handleFilterClick={handleFilterClick}
            organizationAssetsData={organizationAssetsData}
            selectedAssets={selectedAssets}
            handleClickAsset={handleClickAsset}
            inViewRef={inViewRef}
          />
        )}
      </div>
    </div>
  );
};
